import type { FC, PointerEvent } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { sceneInput } from '@/lib/scene-state'

interface DragStageProps {
    className?: string
}

/**
 * Transparent area over the truck. Dragging sideways spins the 3D truck, vertical swipes
 * still scroll the page on touch screens.
 */
export const DragStage: FC<DragStageProps> = ({ className }) => {
    const last = useRef<{ x: number, t: number } | null>(null)

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        event.currentTarget.setPointerCapture(event.pointerId)
        last.current = { x: event.clientX, t: performance.now() }
        sceneInput.dragging = true
        sceneInput.yawVelocity = 0
        sceneInput.lastInteraction = performance.now()
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!last.current) return

        const now = performance.now()
        const delta = (event.clientX - last.current.x) * 0.009
        const seconds = Math.max(8, now - last.current.t) / 1000

        sceneInput.yaw += delta
        sceneInput.yawVelocity = Math.max(-6, Math.min(6, delta / seconds))
        sceneInput.lastInteraction = now
        last.current = { x: event.clientX, t: now }
    }

    const end = () => {
        if (!last.current) return

        if (performance.now() - last.current.t > 90) sceneInput.yawVelocity = 0

        last.current = null
        sceneInput.dragging = false
        sceneInput.lastInteraction = performance.now()
    }

    return (
        <div
            aria-hidden='true'
            className={cn('cursor-grab touch-pan-y select-none active:cursor-grabbing', className)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={end}
            onPointerCancel={end}
            onLostPointerCapture={end}
        />
    )
}

/** Turns the truck by a fixed step, for keyboard and screen reader users. */
export const nudgeTruck = (direction: 1 | -1) => {
    sceneInput.yawVelocity = direction * 2.4
    sceneInput.lastInteraction = performance.now()
}
